import tagsApiRepository from '@/services/conversations/TagsApiRepository';
import TagDto from '@/services/conversations/Dto/TagDto';

// TODO: Direct JSON.parse on values from localStorage can throw when the value is null
// or not valid JSON (e.g., when an AxiosError string is stored). Use safe parsing and
// serialize errors to avoid hard crashes during app bootstrap.
const safeJSONParse = (rawValue, fallbackValue) => {
    try {
        if (rawValue === null || rawValue === undefined) return fallbackValue;
        return JSON.parse(rawValue);
    } catch {
        return fallbackValue;
    }
};

const serializeError = (error) => {
    try {
        if (!error) return null;
        if (error.response && error.response.data) {
            const data = error.response.data;
            if (typeof data === 'string') return data;
            if (data && (data.message || data.error)) return data.message || data.error;
        }
        if (error.message) return error.message;
        return String(error);
    } catch {
        return 'Unknown error';
    }
};

const state = {
    tags: safeJSONParse(localStorage.getItem('tags'), []),
    loading: safeJSONParse(localStorage.getItem('tagsLoading'), false),
    error: safeJSONParse(localStorage.getItem('tagsError'), null),
    initialized: safeJSONParse(localStorage.getItem('tagsInitialized'), false),
};

const getters = {
    allTags: (state) => state.tags,
    getTagById: (state) => (id) => state.tags.find((tag) => tag.uuid === id),
    isLoading: (state) => state.loading,
    hasError: (state) => state.error !== null,
    isInitialized: (state) => state.initialized,
    getMarketingTag: (state) => state.tags.find((tag) => tag.name === 'marketing'),
};

const mutations = {
    SET_TAGS(state, tags) {
        state.tags = tags;
        localStorage.setItem('tags', JSON.stringify(tags));
    },
    SET_LOADING(state, loading) {
        state.loading = loading;
        localStorage.setItem('tagsLoading', JSON.stringify(loading));
    },
    SET_ERROR(state, error) {
        const message = serializeError(error);
        state.error = message;
        localStorage.setItem('tagsError', JSON.stringify(message));
    },
    SET_INITIALIZED(state, initialized) {
        state.initialized = initialized;
        localStorage.setItem('tagsInitialized', JSON.stringify(initialized));
    },
    ADD_TAG(state, tag) {
        if (!state.tags.find((t) => t.uuid === tag.uuid)) {
            state.tags.push(tag);
        }
        localStorage.setItem('tags', JSON.stringify(state.tags));
    },
    UPDATE_TAG(state, updatedTag) {
        const index = state.tags.findIndex((tag) => tag.uuid === updatedTag.uuid);
        if (index !== -1) {
            state.tags.splice(index, 1, updatedTag);
        }
        localStorage.setItem('tags', JSON.stringify(state.tags));
    },
    REMOVE_TAG(state, tagId) {
        state.tags = state.tags.filter((tag) => tag.uuid !== tagId);
        localStorage.setItem('tags', JSON.stringify(state.tags));
    },
};

const actions = {
    async fetchTags({ commit }) {
        commit('SET_LOADING', true);
        try {
            const tags = await tagsApiRepository.getTags();
            commit('SET_TAGS', tags);
            commit('SET_ERROR', null);
            commit('SET_INITIALIZED', true);
        } catch (error) {
            console.error('Error fetching tags:', error);
            commit('SET_ERROR', error);
        } finally {
            commit('SET_LOADING', false);
        }
    },

    async createTag({ commit }, tagRequest) {
        commit('SET_LOADING', true);
        try {
            await tagsApiRepository.createTag(tagRequest);
            const createdTag = await tagsApiRepository.getTag(tagRequest.uuid);
            console.log('Created tag:', createdTag);
            commit('ADD_TAG', createdTag);
            commit('SET_ERROR', null);
            return createdTag;
        } catch (error) {
            console.error('Error creating tag:', error);
            commit('SET_ERROR', error);
            throw error;
        } finally {
            commit('SET_LOADING', false);
        }
    },

    async updateTag({ commit }, { tagId, tagRequest }) {
        commit('SET_LOADING', true);
        try {
            const updatedTag = await tagsApiRepository.updateTag(tagId, tagRequest);
            commit('UPDATE_TAG', updatedTag);
            commit('SET_ERROR', null);
            return updatedTag;
        } catch (error) {
            console.error('Error updating tag:', error);
            commit('SET_ERROR', error);
            throw error;
        } finally {
            commit('SET_LOADING', false);
        }
    },

    async deleteTag({ commit }, tagId) {
        commit('SET_LOADING', true);
        try {
            await tagsApiRepository.deleteTag(tagId);
            commit('REMOVE_TAG', tagId);
            commit('SET_ERROR', null);
        } catch (error) {
            console.error('Error deleting tag:', error);
            commit('SET_ERROR', error);
            throw error;
        } finally {
            commit('SET_LOADING', false);
        }
    },

    async subscribeToTagUpdates({ rootState, dispatch }) {
        const topic = `/account/${rootState.user.account.uuid}/tag`;
        try {
            await dispatch(
                'websocket/subscribe',
                { topic, callback: (data) => dispatch('handleTagEvent', data) },
                { root: true },
            );
        } catch (error) {
            console.error(`Failed to subscribe to ${topic}:`, error);
        }
    },

    unsubscribeFromTagUpdates({ rootState, dispatch }) {
        const topic = `/account/${rootState.user.account.uuid}/tag`;
        dispatch('websocket/unsubscribe', { topic }, { root: true });
    },

    handleTagEvent({ commit }, event) {
        if (!event || !event.data) return;

        // Parse JSON string if it's a string
        const tagData = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;

        // Create tag using builder pattern
        const tag = new TagDto.Builder()
            .withUuid(tagData.uuid)
            .withAccountId(tagData.account_id)
            .withName(tagData.name)
            .withColor(tagData.color)
            .build();

        switch (event.name) {
            case 'TagCreated':
                console.log('TagCreated event:', tag);
                commit('ADD_TAG', tag);
                break;
            case 'TagUpdated':
                commit('UPDATE_TAG', tag);
                break;
            case 'TagDeleted':
                commit('REMOVE_TAG', tag.uuid);
                break;
            default:
                console.warn(`Unhandled tag event: ${event.name}`);
        }
    },

    clearState({ commit }) {
        commit('SET_TAGS', []);
        commit('SET_LOADING', false);
        commit('SET_ERROR', null);
        commit('SET_INITIALIZED', false);
        localStorage.removeItem('tags');
        localStorage.removeItem('tagsLoading');
        localStorage.removeItem('tagsError');
        localStorage.removeItem('tagsInitialized');
    },
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};
